import React, { useRef, useEffect, useCallback, useState } from "react";
import "./VideoPlayer.css";
import { VIDEO_BUFFER } from "../../constants/general";

interface DirectVideoPlayerProps {
  src: string;
  title?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  duration?: number;
  onEnded: () => void;
  onError?: (error: string) => void;
  onTimeUpdate?: (current: number, duration: number) => void;
  className?: string;
  fillMode?: "contain" | "cover";
  showPortraitPosterOnLandscape?: boolean;
  showLandscapePosterOnPortrait?: boolean;
  screenOrientation?: "landscape" | "portrait";
  factoryId?: string;
}

let instanceCounter = 0;

const DirectVideoPlayer: React.FC<DirectVideoPlayerProps> = ({
  src,
  title = "Video",
  autoplay = true,
  muted = true,
  loop = false,
  duration = 30,
  onEnded,
  onError,
  onTimeUpdate,
  className = "direct-video-player",
  fillMode = "cover",
  showPortraitPosterOnLandscape = true,
  showLandscapePosterOnPortrait = false,
  screenOrientation = "landscape",
  factoryId = "unknown",
}) => {
  const instanceId = useRef(`dv-${++instanceCounter}`);
  const videoRef = useRef<HTMLVideoElement>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const completedRef = useRef(false);
  const playbackStartedRef = useRef(false);
  const [videoOrientation, setVideoOrientation] = useState<
    "portrait" | "landscape" | null
  >(null);
  const [videoDimensions, setVideoDimensions] = useState({
    width: 0,
    height: 0,
  });

  // NEW: Ref to track the current source URL
  const currentSrcRef = useRef<string | null>(null);
  // NEW: Ref to track if cleanup is in progress
  const cleanupInProgressRef = useRef(false);

  console.log(
    `🎬 DirectVideoPlayer[${
      instanceId.current
    }] Initializing for "${title}" with source: ${
      src ? (src.length > 50 ? src.substring(0, 50) + "..." : src) : "none"
    }`,
  );

  // Setup and configure video element
  const setupVideo = useCallback(() => {
    if (!videoRef.current) return;
    if (!src) {
      console.error(
        `❌ DirectVideoPlayer[${instanceId.current}] No source URL provided`,
      );
      onError?.("No source URL provided");
      return;
    }

    // Per-invocation guard: unique to each setupVideo() call.
    // When cleanup sets this to false, all stale promise handlers become no-ops.
    let isActive = true;

    // Store current source for tracking
    currentSrcRef.current = src;
    cleanupInProgressRef.current = false;

    const video = videoRef.current;

    // NEW: Clear previous handlers before setting up new ones
    video.onloadstart = null;
    video.onloadedmetadata = null;
    video.onplaying = null;
    video.onpause = null;
    video.onended = null;
    video.onerror = null;

    // Set source and configuration
    video.muted = muted;
    video.autoplay = false; // Don't let the browser auto-start — we'll call play() after canplay
    video.loop = loop;
    video.src = src;
    // Force Safari to begin loading the new source
    video.load();

    console.log(
      `🎬 DirectVideoPlayer[${instanceId.current}] Setting up video:`,
      {
        src: src.substring(0, 50) + (src.length > 50 ? "..." : ""),
        muted,
        autoplay,
        loop,
      },
    );

    // Handle video end
    const handleVideoEnded = () => {
      if (!isActive) return; // Stale invocation — ignore
      console.log(
        `✅ DirectVideoPlayer[${instanceId.current}] Video ended naturally`,
      );
      if (!completedRef.current) {
        completedRef.current = true;
        handleCompletion();
      }
    };

    // Handle video errors
    const handleVideoError = () => {
      if (!isActive) return; // Stale invocation — ignore
      // Don't report errors if cleanup is in progress
      if (cleanupInProgressRef.current) {
        console.log(
          `⏩ DirectVideoPlayer[${instanceId.current}] Ignoring error during cleanup`,
        );
        return;
      }

      const errorMsg = `Video error: ${
        video.error?.message || "Unknown error"
      } (code: ${video.error?.code || "unknown"})`;
      console.error(`❌ DirectVideoPlayer[${instanceId.current}] ${errorMsg}`);
      onError?.(errorMsg);

      if (!completedRef.current) {
        completedRef.current = true;
        handleCompletion();
      }
    };

    // Handle video metadata load to determine orientation
    const handleMetadataLoaded = () => {
      if (video.videoWidth && video.videoHeight) {
        const isPortraitVideo = video.videoHeight > video.videoWidth;
        setVideoOrientation(isPortraitVideo ? "portrait" : "landscape");
        setVideoDimensions({
          width: video.videoWidth,
          height: video.videoHeight,
        });
        console.log(
          `📏 DirectVideoPlayer[${instanceId.current}] Video dimensions: ${
            video.videoWidth
          }x${video.videoHeight}, orientation: ${
            isPortraitVideo ? "portrait" : "landscape"
          }`,
        );
      }
    };

    // Handle playback started
    const handlePlaying = () => {
      playbackStartedRef.current = true;
      console.log(
        `▶️ DirectVideoPlayer[${instanceId.current}] Playback started`,
      );
    };

    // Add event listeners
    video.addEventListener("ended", handleVideoEnded);
    video.addEventListener("error", handleVideoError);
    video.addEventListener("loadedmetadata", handleMetadataLoaded);
    video.addEventListener("playing", handlePlaying);
    // Add timeupdate event for progress bar sync
    if (typeof onTimeUpdate === "function") {
      video.addEventListener("timeupdate", timeUpdateHandler);
    }
    // Handler for timeupdate event, defined at component level for stable reference
    function timeUpdateHandler(this: HTMLVideoElement) {
      if (typeof onTimeUpdate === "function") {
        onTimeUpdate(this.currentTime, this.duration || duration);
      }
    }

    // Set fallback timer in case video playback stalls
    if (durationTimerRef.current) {
      clearTimeout(durationTimerRef.current);
    }

    const safetyDuration = duration + VIDEO_BUFFER; // Add 5 seconds buffer
    console.log(
      `⏱️ DirectVideoPlayer[${instanceId.current}] Setting fallback timer: ${safetyDuration}s`,
    );

    durationTimerRef.current = setTimeout(() => {
      console.log(
        `⏱️ DirectVideoPlayer[${instanceId.current}] Fallback timer expired`,
      );
      if (!completedRef.current) {
        completedRef.current = true;
        handleCompletion();
      }
    }, safetyDuration * 1000);

    // Wait for the video to be ready before playing.
    // Safari corrupts the <video> element if play() is called before the source is loaded.
    const handleCanPlay = () => {
      video.removeEventListener("canplay", handleCanPlay);
      if (!isActive) return; // Stale invocation — component was cleaned up
      console.log(
        `✅ DirectVideoPlayer[${instanceId.current}] canplay fired — starting playback`,
      );
      video.play().catch((err) => {
        if (!isActive) return;
        console.warn(
          `⚠️ DirectVideoPlayer[${instanceId.current}] Autoplay failed, trying muted:`,
          err,
        );
        video.muted = true;
        video.play().catch((err2) => {
          if (!isActive) return;
          console.error(
            `❌ DirectVideoPlayer[${instanceId.current}] Even muted autoplay failed:`,
            err2,
          );
        });
      });
    };
    video.addEventListener("canplay", handleCanPlay);

    // Return cleanup function
    return () => {
      // Invalidate all closures from this setupVideo() invocation
      isActive = false;
      // Flag that cleanup is in progress
      cleanupInProgressRef.current = true;

      // Remove all event listeners
      video.removeEventListener("ended", handleVideoEnded);
      video.removeEventListener("error", handleVideoError);
      video.removeEventListener("loadedmetadata", handleMetadataLoaded);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("canplay", handleCanPlay);
      if (typeof onTimeUpdate === "function") {
        video.removeEventListener("timeupdate", timeUpdateHandler);
      }

      // Pause the video — but do NOT clear the source or call load().
      // Safari puts <video> into an unrecoverable error state (MEDIA_ERR_SRC_NOT_SUPPORTED)
      // if you do video.src = ""; video.load(). The next setupVideo() will overwrite src naturally.
      try {
        video.pause();
        console.log(`⏸️ DirectVideoPlayer[${instanceId.current}] Video paused`);
      } catch (e) {
        console.warn(
          `⚠️ DirectVideoPlayer[${instanceId.current}] Error pausing video:`,
          e,
        );
      }
    };
  }, [src, muted, autoplay, loop, duration, onError]);

  // Handle completion and cleanup
  const handleCompletion = useCallback(() => {
    console.log(
      `🏁 DirectVideoPlayer[${instanceId.current}] Handling completion`,
    );

    // Clear any pending timers
    if (durationTimerRef.current) {
      clearTimeout(durationTimerRef.current);
      durationTimerRef.current = null;
    }

    // Just pause — don't clear source (Safari can't recover from video.src = ""; video.load())
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (e) {
        // ignore
      }
    }

    // Call the onEnded callback after a small delay to ensure cleanup completes
    setTimeout(() => {
      onEnded();
    }, 50);
  }, [onEnded, src]);

  // Initialize video on mount
  useEffect(() => {
    completedRef.current = false;
    playbackStartedRef.current = false;
    cleanupInProgressRef.current = false;

    const cleanup = setupVideo();

    return () => {
      console.log(
        `🧹 DirectVideoPlayer[${instanceId.current}] Cleaning up component`,
      );
      completedRef.current = true;

      // Clear any pending timers
      if (durationTimerRef.current) {
        clearTimeout(durationTimerRef.current);
        durationTimerRef.current = null;
      }

      // Run the cleanup function if it exists
      if (cleanup) {
        cleanup();
      }
    };
  }, [setupVideo]);

  // Determine the right objectFit style based on video and screen orientation
  const getObjectFitStyle = (): "cover" | "contain" | "fill" => {
    // Default to provided fillMode if video orientation hasn't been detected yet
    if (!videoOrientation) return fillMode || "cover";

    // If screen is landscape and video is portrait
    if (screenOrientation === "landscape" && videoOrientation === "portrait") {
      // Use contain to show full portrait video on landscape screen if specified
      return showPortraitPosterOnLandscape ? "contain" : "cover";
    }

    // If screen is portrait and video is landscape
    if (screenOrientation === "portrait" && videoOrientation === "landscape") {
      // Use contain to show full landscape video on portrait screen if specified
      return showLandscapePosterOnPortrait ? "contain" : "cover";
    }

    // If orientations match (portrait-portrait or landscape-landscape)
    return fillMode || "cover";
  };

  // Add a CSS class based on video orientation
  const getOrientationClass = (): string => {
    if (!videoOrientation) return "";
    return videoOrientation === "portrait"
      ? "video-portrait"
      : "video-landscape";
  };

  // Log the object-fit style being used
  useEffect(() => {
    if (videoOrientation) {
      console.log(
        `🎬 DirectVideoPlayer[${
          instanceId.current
        }] Using object-fit: ${getObjectFitStyle()} for ${videoOrientation} video on ${screenOrientation} screen`,
      );
    }
  }, [videoOrientation, screenOrientation]);

  return (
    <div
      className={`${className} video-container`}
      style={{ width: "100%", height: "100%" }}
    >
      <video
        ref={videoRef}
        className={getOrientationClass()}
        style={{
          width: "100%",
          height: "100%",
          objectFit: getObjectFitStyle(),
        }}
        playsInline
        controls={false}
        data-testid="direct-video-player"
      />

      {/* Add debug overlay in development mode */}
      {process.env.NODE_ENV === "development" && (
        <div className="video-debug-overlay">
          {title} | {videoOrientation || "detecting"} | {instanceId.current}
        </div>
      )}
    </div>
  );
};

export default React.memo(DirectVideoPlayer);
